import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { styled, useMediaQuery } from '@mui/material';
import { ChatBubbleOutline } from '@mui/icons-material';
import { Colors } from 'library';
import { Avatar, Caption, EmptyText, PageHeader, Row, SearchInput, Stack, Text, Title } from 'library/ui';
import client from 'shared/ApiClient';
import ProviderMessenger from './ProviderMessenger';
import { consoleLogInDev, FeatureFlag, useFeatureFlag } from 'shared';
import { useUserContext } from 'UserContext';

interface MessagesParams {
    id: string;
}

interface IThread {
    bot_flag: boolean;
    family_id: number;
    image: string;
    message: string;
    name: string;
    read: boolean;
    parent_read: boolean;
    sitter_id: number;
    timestamp: string;
    completed_background_check: boolean;
}

const Messages = () => {
    const { id } = useParams<MessagesParams>();
    const { role } = useUserContext();
    const [threads, setThreads] = useState<IThread[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [currentUser, setCurrentUser] = useState<any>();
    const [currentThread, setCurrentThread] = useState<IThread>();
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>('');
    const isMobile = useMediaQuery('(max-width:700px)');
    const { featureActive: chatwootEnabled } = useFeatureFlag(FeatureFlag.CENTER_CHATWOOT_ENABLED);

    const getMessageThreads = () => {
        client('unique-recipients/')
            .then((response: IThread[]) => {
                let mappedResponse = chatwootEnabled ? response.filter((x) => !x.bot_flag) : response;
                mappedResponse = mappedResponse.map((x) => {
                    return { ...x, read: x.family_id === parseInt(id) ? true : x.read };
                });
                setThreads(mappedResponse);
                if (id) {
                    const thread = response.find((x) => x.family_id === parseInt(id));
                    if (!thread) {
                        getNewThreadInfo(id);
                    } else {
                        setCurrentThread(thread);
                        setCurrentUser({ user: { id: parseInt(id) } });
                    }
                }
            })
            .catch(consoleLogInDev)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        getMessageThreads();
        const button: HTMLElement | null = document?.getElementById(id);
        if (button) {
            button.scrollIntoView();
        }
    }, [id]);

    const getNewThreadInfo = (userId: string) => {
        client(`api/babysitters/?user_id=${userId}`).then((response) => {
            const provider = response[0];
            const newThread = {
                family_id: provider.user.id,
                name: `${provider.user.first_name} ${provider.user.last_name?.[0] ?? ''}`,
                image: provider.user.profile_picture?.image,
                read: false,
                parent_read: true,
                sitter_id: -1,
                bot_flag: false,
                timestamp: '',
                message: '',
                completed_background_check: provider.completed_background_check,
            };
            setThreads((prevValue) => [...prevValue.slice(0, 1), newThread, ...prevValue.slice(1)]);
            setCurrentThread(newThread);
            setCurrentUser({ user: { id: parseInt(id) } });
        });
    };

    if (role !== 'business_active') return null;

    const showList = !isMobile || !id;
    const showConversation = !isMobile || !!id;

    const query = search.trim().toLowerCase();
    const filteredThreads = query ? threads.filter((x) => x.name.toLowerCase().includes(query)) : threads;

    return (
        <Stack gap="none">
            <PageHeader title="Messages" />
            <Shell>
                {showList && (
                    <ListPanel>
                        <Stack padding="sm">
                            <SearchInput value={search} onChange={setSearch} placeholder="Search conversations..." />
                        </Stack>
                        <ThreadScroll>
                            {loading ? (
                                <Stack padding="md">
                                    <EmptyText padding="none">Loading…</EmptyText>
                                </Stack>
                            ) : threads.length === 0 ? (
                                <Stack padding="md">
                                    <EmptyText padding="none">
                                        You have no messages to view. Messages from workers will appear here when they
                                        message during a shift.
                                    </EmptyText>
                                </Stack>
                            ) : filteredThreads.length === 0 ? (
                                <Stack padding="md">
                                    <EmptyText padding="none">No conversations match your search.</EmptyText>
                                </Stack>
                            ) : (
                                filteredThreads.map((x) => (
                                    <ThreadItem
                                        key={x.family_id}
                                        id={x.family_id.toString()}
                                        to={`/messages/${x.family_id}`}
                                        $selected={x.family_id === parseInt(id)}
                                    >
                                        <Avatar imageUrl={x.image} name={x.name} size={44} />
                                        <Stack gap="none" grow>
                                            <Row justify="between" align="center" gap="sm">
                                                <Text weight="semibold" truncate>
                                                    {x.name}
                                                </Text>
                                                {!x.parent_read && <UnreadDot />}
                                            </Row>
                                            {!!x.timestamp && <Caption>{x.timestamp}</Caption>}
                                            {!!x.message && (
                                                <Text size="sm" tone="secondary" truncate>
                                                    {x.message}
                                                </Text>
                                            )}
                                        </Stack>
                                    </ThreadItem>
                                ))
                            )}
                        </ThreadScroll>
                    </ListPanel>
                )}

                {showConversation &&
                    (currentUser ? (
                        <ProviderMessenger
                            providerIdObject={currentUser}
                            onMessageSent={getMessageThreads}
                            providerName={currentThread?.name}
                            providerImage={currentThread?.image}
                            completedBackgroundCheck={currentThread?.completed_background_check || false}
                        />
                    ) : (
                        <Stack grow align="center" justify="center" gap="sm">
                            <ChatBubbleOutline sx={{ fontSize: 48, color: Colors.border }} />
                            <Title size="h3">Select a conversation</Title>
                            <Text tone="secondary">Choose a worker from the list to view your messages.</Text>
                        </Stack>
                    ))}
            </Shell>
        </Stack>
    );
};

export default Messages;

const Shell = styled('div')({
    display: 'flex',
    height: 'calc(100vh - 180px)',
    border: `1px solid ${Colors.border}`,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: Colors.white,
});

const ListPanel = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: 'min(340px, 100%)',
    flexShrink: 0,
    borderRight: `1px solid ${Colors.border}`,
    '@media (max-width:700px)': {
        width: '100%',
        borderRight: 'none',
    },
});

const ThreadScroll = styled('div')({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
});

const ThreadItem = styled(RouterLink, { shouldForwardProp: (p) => p !== '$selected' })<{ $selected: boolean }>(
    ({ $selected }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        textDecoration: 'none',
        backgroundColor: $selected ? Colors.surface : 'transparent',
        borderLeft: `3px solid ${$selected ? Colors.primary : 'transparent'}`,
        borderBottom: `1px solid ${Colors.border}`,
        '&:hover': { backgroundColor: Colors.surface },
    }),
);

const UnreadDot = styled('span')({
    flexShrink: 0,
    height: 12,
    width: 12,
    borderRadius: '50%',
    backgroundColor: Colors.error,
});
